import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { NavigationHistoryProvider } from '@/components/providers/navigation-history-provider';
import { MessageProvider } from '@/components/providers/message-provider';
import { useAuthHydration } from "@/hooks/use-auth-hydration";
import { UserRole } from "@comp-sys/shared";
import { PROFILE_CREATABLE_ROLES, NON_PROFILE_ROLES } from '@/lib/utils/permissions';
import { useAuthStore } from "@/stores/auth-store";
import { useEffect, useState } from "react";
import { ImpersonateBanner } from '@/components/shared/impersonate-banner';
import { useTranslations } from '@/i18n/useTranslations';
import { AppShell } from '@/components/shell/app-shell';
import { GuidedShell } from '@/components/shell/guided/guided-shell';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useGuidedUiMode } from '@/hooks/use-guided-ui-mode';
import { isGuidedEligible } from '@/lib/utils/guided-state';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

/**
 * 仪表盘布局（路由层）。
 *
 * 本组件只负责**与 shell 无关**的横切逻辑：鉴权水合、各类重定向（档案/姓名/学校/机构绑定）、
 * 路由滚动复位、impersonate 横幅偏移、skip-link，以及把页面级 Provider（MessageProvider）挂在过渡之外。
 *
 * **布局几何与导航范式下沉到 `<AppShell>`**：它按当前形态家族（data-skin）选择结构化 shell
 * （glass/refined/soft/neon/ink/luxury）。同一份业务逻辑，不同的空间语法——这是「换应用 vs 换皮」的分水岭。
 */
export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const { isHydrated, accessToken } = useAuthHydration();
  const tCommon = useTranslations('common');
  // 引导式模式(第四维 uiMode):仅引导式资格角色 + uiMode=guided 时启用,否则零回归经典 shell。
  const uiMode = useUiModeStore((s) => s.uiMode);
  const applyGuidedConfig = useUiModeStore((s) => s.applyGuidedConfig);
  const guided = uiMode === 'guided' && isGuidedEligible(user?.activeRole);

  // admin 全局实验性配置收敛 uiMode(集中在 store.applyGuidedConfig:off/force 强制并清 userChosen,
  // button/default 尊重用户显式选择否则用模式默认)。等配置加载完(undefined 时不动作,避免误清用户 toggle)。
  // 现有各 guided gate 读 uiMode,故同步后自动遵循 admin 配置,无需逐个改 gate。
  const guidedUiMode = useGuidedUiMode();
  useEffect(() => {
    if (guidedUiMode !== undefined) applyGuidedConfig(guidedUiMode);
  }, [guidedUiMode, applyGuidedConfig]);

  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const check = () => setIsImpersonating(!!sessionStorage.getItem("impersonator"));
    check();
    window.addEventListener("storage", check);
    return () => window.removeEventListener("storage", check);
  }, []);

  // Redirect to profile setup if needed
  useEffect(() => {
    if (!isHydrated) return;
    if (!accessToken) return;

    if (user && PROFILE_CREATABLE_ROLES.includes(user.activeRole as UserRole) && !user.hasCompletedOnboarding) {
      navigate("/profile-setup", { replace: true });
    }
  }, [isHydrated, accessToken, user, navigate]);

  // Redirect non-profile users without onboarding to name setup
  useEffect(() => {
    if (!isHydrated) return;
    if (!accessToken) return;
    if (window.location.pathname.includes('school-no-binding')) return;

    if (user && NON_PROFILE_ROLES.includes(user.activeRole as UserRole) && !user.hasCompletedOnboarding) {
      navigate("/name-setup", { replace: true });
    }
  }, [isHydrated, accessToken, user, navigate]);

  // Redirect unbound SCHOOL users
  useEffect(() => {
    if (!isHydrated) return;
    if (!accessToken) return;
    if (!user || user.activeRole !== 'SCHOOL') return;
    if (user.hasCompletedOnboarding === false) return;
    if (window.location.pathname.includes('school-no-binding')) return;

    if (!user.schoolId) {
      navigate("/dashboard/school-no-binding", { replace: true });
    }
  }, [isHydrated, accessToken, user, navigate]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Redirect unbound INSTITUTION users
  useEffect(() => {
    if (!isHydrated) return;
    if (!accessToken) return;
    if (!user || user.activeRole !== 'INSTITUTION') return;
    if (user.hasCompletedOnboarding === false) return;
    if (window.location.pathname.includes('institution-no-binding')) return;

    if (!user.institutionId) {
      navigate("/dashboard/institution-no-binding", { replace: true });
    }
  }, [isHydrated, accessToken, user, navigate]);

  // Show loading while checking hydration
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-card">
            <Loader2 className="w-6 h-6 text-primary-foreground animate-spin" />
          </div>
          <p className="text-sm text-muted-foreground font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <NavigationHistoryProvider>
      {isImpersonating && <ImpersonateBanner onExitComplete={() => setIsImpersonating(false)} />}
      <div className={cn("min-h-screen bg-background", isImpersonating && "pt-10")}>
        {/* Skip navigation link for keyboard accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-overlay/80 focus: focus:backdrop-blur-xl focus:border focus:border-surface focus:dark:border-border focus:shadow-glass focus:text-sm focus:font-medium focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          {tCommon('skipToContent')}
        </a>
        {/* MessageProvider 挂在 shell 之外：其 unread 拉取 effect 不应随路由过渡重挂载。
            shell 只负责布局几何/导航范式；页面过渡由各家族 shell 自行包裹 <Outlet/>。 */}
        <MessageProvider>
          {guided ? (
            <GuidedShell>
              <Outlet />
            </GuidedShell>
          ) : (
            <AppShell>
              <Outlet />
            </AppShell>
          )}
        </MessageProvider>
      </div>
    </NavigationHistoryProvider>
  );
}
